/**
 * Gemini credentials form — the registry's one secret field (apiKey) on a
 * single screen, mounted via show() as an overlay — mirrors
 * lettermint-credentials-form.view.ts's "form" shape rather than
 * revolut-wizard.view.ts's, since Gemini has no environment axis and no
 * save-time operational input beyond the credential itself
 * (SaveIntegrationInput's gemini variant carries only `id` and `secrets`).
 *
 * Mountable and zero-I/O: `status`/`saving`/`hasStoredCredential` are plain
 * properties the mounting card
 * (web/src/admin/settings/integrations/gemini/gemini-integration.view.ts)
 * hands down from the SAME integrationRows projection the generic
 * Integrations panel itself renders for this row — never a second, parallel
 * status channel. The actual save leaves this package through
 * `geminiSaveIntent$` (./gemini.state.ts); a mounted app-side `.sync` is the
 * sole subscriber, forwarding it into the existing saveIntegrationAction$
 * this package cannot import.
 *
 * A blank field means "keep the stored key" once one is already stored
 * (hasStoredCredential) — this view never pre-fills the field, since no
 * value is ever read back. No overwrite confirm(): unlike Revolut's live
 * secret key (breaks checkout) or Apple Wallet's signing certificate
 * (breaks pass issuance), a wrong or rotated Gemini key only fails the two
 * features that call it (artwork generation, legal-document review) until
 * it's corrected — low enough blast radius that the extra click those two
 * fields' ceremony costs would only be friction here.
 *
 * Localized via LocalizeMixin, matching every sibling wizard/form in this
 * set.
 */
import { overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { html, type PropertyValues, type TemplateResult } from "lit";
import { property, state } from "lit/decorators.js";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { organizationId } from "../../workspace/organization.state.ts";
import {
	INTEGRATION_TONE_CLASS,
	type IntegrationStatusLine,
	NOT_CONFIGURED,
} from "../status.state.ts";
import { geminiSaveIntent$ } from "./gemini.state.ts";

@schmancyElement("gemini-credentials-form")
export class GeminiCredentialsForm extends LocalizeMixin(SchmancyElement) {
	/** The organization this form opened under, read once at construction. */
	private readonly orgId = organizationId.value;

	@property({ attribute: false }) status: IntegrationStatusLine =
		NOT_CONFIGURED;

	@property({ type: Boolean }) saving = false;
	@property({ attribute: false }) lastSaveOk: boolean | null = null;

	/** True only once a key has previously been proven and stored — what
	 *  makes a blank field mean "keep the stored one" rather than "nothing to
	 *  save". */
	@property({ type: Boolean }) hasStoredCredential = false;

	/** Dies with the overlay — this save's own one-shot input. */
	@state() private apiKey = "";
	/** Whether a save has been attempted from THIS mount — the footer's
	 *  "done" state belongs to a save the operator just made, not to a prior
	 *  one whose verified status was already on the row when the overlay
	 *  opened. Mirrors revolut-wizard.view.ts's own `attempted`. */
	@state() private attempted = false;
	@state() private admitted = false;
	@state() private actionHeight: number | null = null;

	override willUpdate(changed: PropertyValues<this>) {
		super.willUpdate(changed);
		if (changed.has("saving") && !this.saving) this.admitted = false;
	}

	private close(): void {
		this.dispatchEvent(
			new CustomEvent("close", { bubbles: true, composed: true }),
		);
	}

	private save(apiKey: string): void {
		if (this.saving || this.admitted) return;
		this.attempted = true;
		// A blank field omits the KEY entirely — the save contract resolves an
		// absent field against the stored one and rejects an empty string.
		this.actionHeight =
			this.shadowRoot?.firstElementChild?.getBoundingClientRect().height ??
			null;
		this.admitted = true;
		geminiSaveIntent$.next({
			orgId: this.orgId,
			...(apiKey.length > 0 && { apiKey }),
		});
		this.attempted = true;
	}

	override render(): TemplateResult {
		const trimmed = this.apiKey.trim();
		const canSubmit = trimmed.length > 0 || this.hasStoredCredential;
		const dateLabel = this.status.at
			? this.status.at
					.toDate()
					.toLocaleDateString(this.uiLanguage === "de" ? "de-DE" : "en-GB", {
						day: "numeric",
						month: "long",
						year: "numeric",
					})
			: null;
		const footerState: "save" | "verifying" | "done" =
			this.saving || this.admitted
				? "verifying"
				: this.attempted && this.lastSaveOk === true
					? "done"
					: "save";

		return html`
			<div style=${this.actionHeight === null ? "" : `height:${this.actionHeight}px`} class="flex h-full max-h-[85vh] w-md max-w-full flex-col">
				<div class="flex items-center justify-end px-5 pt-4">
					<schmancy-icon-button
						icon="close"
						variant="text"
						?disabled=${this.saving}
						aria-label=${this.t("admin.integrations.gemini.form.closeAriaLabel")}
						@click=${() => this.close()}
					></schmancy-icon-button>
				</div>

				<div ${overflowWithin()} class="flex min-h-0 flex-1 flex-col gap-4 px-5 pb-24">
					<schmancy-typography type="body" token="sm" class=${INTEGRATION_TONE_CLASS[this.status.tone]}>
						${this.t(this.status.message, this.status.params)}${dateLabel ? ` · ${dateLabel}` : ""}
					</schmancy-typography>

					<schmancy-input
                        ?readonly=${this.saving || this.admitted}
						type="password"
						autocomplete="off"
						?required=${!this.hasStoredCredential}
						label=${this.t("admin.integrations.gemini.form.apiKeyLabel")}
						.value=${this.apiKey}
						@input=${(e: CustomEvent<{ value: string }>) => {
							if (e.detail.value === this.apiKey) return;
							this.apiKey = e.detail.value;
						}}
						hint=${this.t(
							this.hasStoredCredential
								? "admin.integrations.panel.secretKeepHint"
								: "admin.integrations.panel.secretWriteOnlyHint",
						)}
					></schmancy-input>
				</div>

				<schmancy-surface opacity="translucent" elevated class="sticky bottom-0 flex items-center gap-2 px-5 py-4 pt-2">
                    <schmancy-button
                        class="ml-auto"
                        variant="filled"
                        color="primary"
                        .busy=${this.saving || this.admitted}
                        ?disabled=${footerState !== "done" && !canSubmit}
                        @click=${() => (footerState === "done" ? this.close() : this.save(trimmed))}
                    >
                        ${this.t(footerState === "done" ? "admin.integrations.gemini.form.closeButton" : "admin.integrations.gemini.form.saveButton")}
                    </schmancy-button>
				</schmancy-surface>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"gemini-credentials-form": GeminiCredentialsForm;
	}
}
